/**
 * Per-metric presentation (tone + icon) for the admin dashboard.
 *
 * Blue-led. The blue family carries inflow, volume, revenue and profit; three
 * semantic families are deliberately held back from it so the dashboard stays
 * scannable:
 *   orange = money leaving · rose = risk and debt · amber = credits (gold)
 *
 * Tailwind class strings must stay literal — the JIT compiler scans this file
 * as source text and cannot resolve interpolated class names.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertTriangle,
  ArrowDownCircle,
  ArrowLeftRight,
  ArrowUpCircle,
  Banknote,
  Clock,
  Coins,
  Landmark,
  Percent,
  PiggyBank,
  Receipt,
  Scale,
  ShieldAlert,
  Store,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import type { MetricId } from './metricDefs';

export type MetricTone =
  | 'navy'
  | 'blue'
  | 'sky'
  | 'indigo'
  | 'emerald'
  | 'orange'
  | 'amber'
  | 'violet'
  | 'rose'
  | 'slate';

export type ToneStyle = {
  surface: string;
  chipBg: string;
  chipText: string;
  value: string;
};

export const TONE_STYLES: Record<MetricTone, ToneStyle> = {
  /** Headline profit — the only tone with a solid chip, so the hero card leads. */
  navy: {
    surface: 'bg-blue-50',
    chipBg: 'bg-blue-600',
    chipText: 'text-white',
    value: 'text-blue-900',
  },
  blue: {
    surface: 'bg-blue-50/60',
    chipBg: 'bg-blue-100',
    chipText: 'text-blue-700',
    value: 'text-blue-800',
  },
  sky: {
    surface: 'bg-sky-50/60',
    chipBg: 'bg-sky-100',
    chipText: 'text-sky-700',
    value: 'text-sky-800',
  },
  indigo: {
    surface: 'bg-indigo-50/60',
    chipBg: 'bg-indigo-100',
    chipText: 'text-indigo-700',
    value: 'text-indigo-800',
  },
  emerald: {
    surface: 'bg-emerald-50/50',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-700',
    value: 'text-emerald-800',
  },
  orange: {
    surface: 'bg-orange-50/50',
    chipBg: 'bg-orange-100',
    chipText: 'text-orange-700',
    value: 'text-orange-800',
  },
  amber: {
    surface: 'bg-amber-50/50',
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-700',
    value: 'text-amber-800',
  },
  violet: {
    surface: 'bg-violet-50/50',
    chipBg: 'bg-violet-100',
    chipText: 'text-violet-700',
    value: 'text-violet-800',
  },
  rose: {
    surface: 'bg-rose-50/50',
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-700',
    value: 'text-rose-800',
  },
  slate: {
    surface: 'bg-slate-50/70',
    chipBg: 'bg-slate-100',
    chipText: 'text-slate-600',
    value: 'text-slate-900',
  },
};

const TONES: Partial<Record<MetricId, MetricTone>> = {
  // Profit and revenue
  platformNetPnl: 'navy',
  shopNetProfit: 'navy',
  feesEarned: 'sky',
  platformFeeRevenue: 'sky',
  netCashFlow: 'indigo',

  // Money in
  usdIn: 'blue',
  wholesaleOnlineUsd: 'blue',
  hierarchyCashCollected: 'blue',
  playerMoneyIn: 'blue',
  depositsAmount: 'blue',
  depositsCount: 'blue',
  avgDeposit: 'blue',
  onlineDepositsNetUsd: 'blue',
  onlineDepositsCount: 'blue',
  counterDepositsCashReceived: 'sky',
  cashExpected: 'blue',
  cashReceived: 'sky',
  tierUsdFloatTotal: 'blue',

  // Network volume
  creditPurchasesTotal: 'indigo',
  creditPurchasesSuperAgent: 'blue',
  creditPurchasesAgent: 'blue',
  creditPurchasesShop: 'blue',

  // Credits stay gold — they are not USD and should not read as cash.
  creditsIssued: 'amber',
  counterDepositsCredits: 'amber',
  counterRedemptionsCredits: 'amber',

  // Money out stays warm so outflow never blends into the blue.
  payoutsOut: 'orange',
  withdrawalsAmount: 'orange',
  withdrawalsCount: 'orange',
  shopSpent: 'orange',
  onlineWithdrawalsGrossUsd: 'orange',
  onlineWithdrawalsCount: 'orange',
  counterRedemptionsCashPaid: 'orange',

  // Risk stays rose — the only alarm hue.
  pendingWithdrawalsCount: 'rose',
  pendingWithdrawalsAmount: 'rose',
  playersPendingWithdrawals: 'rose',
  shopFundingDebtOutstanding: 'rose',
  onlineDebtOutstanding: 'rose',
  cashUncollected: 'rose',
  depositFailedRatePct: 'rose',
  tierFundingUsd: 'rose',
  exposure: 'rose',

  // Player population — blue is the people colour across the portals.
  totalPlayers: 'blue',
  newPlayers: 'indigo',
  activePlayers: 'sky',

  // Neutral snapshots
  playerCreditLiability: 'slate',
};

const ICONS: Partial<Record<MetricId, LucideIcon>> = {
  platformNetPnl: TrendingUp,
  feesEarned: Percent,
  hierarchyCashCollected: Banknote,
  wholesaleOnlineUsd: Landmark,
  usdIn: ArrowDownCircle,
  payoutsOut: ArrowUpCircle,
  netCashFlow: ArrowLeftRight,
  creditsIssued: Coins,
  pendingWithdrawalsCount: Clock,
  pendingWithdrawalsAmount: Clock,
  shopFundingDebtOutstanding: ShieldAlert,
  exposure: AlertTriangle,
  depositsAmount: ArrowDownCircle,
  depositsCount: Receipt,
  avgDeposit: Scale,
  depositFailedRatePct: AlertTriangle,
  withdrawalsAmount: ArrowUpCircle,
  withdrawalsCount: Receipt,
  playersPendingWithdrawals: Clock,
  playerCreditLiability: PiggyBank,
  playerMoneyIn: ArrowDownCircle,
  shopNetProfit: TrendingUp,
  shopSpent: ArrowUpCircle,
  onlineDepositsNetUsd: ArrowDownCircle,
  counterDepositsCashReceived: Banknote,
  counterDepositsCredits: Coins,
  onlineWithdrawalsGrossUsd: ArrowUpCircle,
  counterRedemptionsCashPaid: Banknote,
  counterRedemptionsCredits: Coins,
  tierFundingUsd: Wallet,
  creditPurchasesTotal: Coins,
  creditPurchasesSuperAgent: Coins,
  creditPurchasesAgent: Coins,
  creditPurchasesShop: Store,
  platformFeeRevenue: Percent,
  cashExpected: Banknote,
  cashReceived: Banknote,
  cashUncollected: AlertTriangle,
  onlineDebtOutstanding: ShieldAlert,
  tierUsdFloatTotal: Landmark,
  totalPlayers: Users,
  newPlayers: UserPlus,
  activePlayers: Activity,
};

export function metricTone(id: MetricId): MetricTone {
  return TONES[id] || 'slate';
}

export function metricIcon(id: MetricId): LucideIcon | undefined {
  return ICONS[id];
}

export function toneStyle(tone: MetricTone): ToneStyle {
  return TONE_STYLES[tone];
}
